import { Link, useLocation } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { Menu, X, Shield, ChevronDown } from 'lucide-react'

const navigation = [
  { name: 'Features', href: '/features' },
  { name: 'Compliance', href: '/compliance' },
  { name: 'Billing', href: '/billing' },
  { name: 'AI Governance', href: '/ai-governance' },
  { name: 'Security', href: '/security' },
  { name: 'Pricing', href: '/pricing' },
]

export default function PublicLayout({ children }: { children: React.ReactNode }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const location = useLocation()
  /* BEGIN SCROLL RESTORE FIX */
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [location.pathname])
  /* END SCROLL RESTORE FIX */

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed inset-x-0 top-0 z-50 bg-white/95 backdrop-blur border-b border-gray-100 pwa-safe-top">
        <nav className="mx-auto flex max-w-7xl items-center justify-between p-4 lg:px-8">
          <div className="flex lg:flex-1">
            <Link to="/" className="-m-1.5 p-1.5 flex items-center gap-2">
              <Shield className="h-8 w-8 text-primary-600" />
              <span className="font-semibold text-xl text-gray-900">Grace Alone ABA</span>
            </Link>
          </div>

          <div className="flex lg:hidden">
            <button
              type="button"
              className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700 touch-target"
              onClick={() => setMobileMenuOpen(true)}
            >
              <span className="sr-only">Open main menu</span>
              <Menu className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>

          <div className="hidden lg:flex lg:gap-x-8">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`text-sm font-medium transition-colors ${
                  location.pathname === item.href
                    ? 'text-primary-600'
                    : 'text-gray-700 hover:text-primary-600'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </div>

          <div className="hidden lg:flex lg:flex-1 lg:justify-end lg:gap-x-4">
            <Link
              to="/login"
              className="text-sm font-medium text-gray-700 hover:text-primary-600"
            >
              Sign in
            </Link>
            <Link
              to="/contact"
              className="btn-primary text-sm"
            >
              Request Demo
            </Link>
          </div>
        </nav>

      </header>

      {/* Mobile menu — moved out of <header> so the fixed-positioned drawer
           isn't visually clipped or stacking-ordered against the header bar. */}
      {mobileMenuOpen && (
        <div className="lg:hidden">
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-[60] bg-black/40"
            onClick={() => setMobileMenuOpen(false)}
            aria-hidden="true"
          />
          {/* Drawer */}
          <div className="fixed inset-y-0 right-0 z-[70] w-full max-w-sm overflow-y-auto bg-white shadow-xl ring-1 ring-gray-900/10">
            <div className="flex items-center justify-between px-6 pt-4 pb-3 border-b border-gray-100">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-2"
              >
                <Shield className="h-8 w-8 text-primary-600" />
                <span className="font-semibold text-xl">Grace Alone ABA</span>
              </Link>
              <button
                type="button"
                className="rounded-md p-2 text-gray-700 hover:bg-gray-100"
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="sr-only">Close menu</span>
                <X className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
            <nav className="flex flex-col px-2 py-4 space-y-1">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className="block rounded-lg px-4 py-3 text-base font-medium text-gray-900 hover:bg-gray-50"
                  onClick={() => {
                    setMobileMenuOpen(false)
                    window.scrollTo({ top: 0, behavior: 'smooth' })
                  }}
                >
                  {item.name}
                </Link>
              ))}
            </nav>
            <div className="border-t border-gray-100 px-4 py-4 space-y-2">
              <Link
                to="/login"
                className="block rounded-lg px-4 py-3 text-base font-medium text-gray-900 hover:bg-gray-50"
                onClick={() => setMobileMenuOpen(false)}
              >
                Sign in
              </Link>
              <Link
                to="/contact"
                className="block btn-primary text-center"
                onClick={() => setMobileMenuOpen(false)}
              >
                Request Demo
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      <main className="pt-16">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white">
        <div className="mx-auto max-w-7xl px-6 py-12 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <Shield className="h-8 w-8 text-primary-400" />
                <span className="font-semibold text-lg">Grace Alone ABA</span>
              </div>
              <p className="text-sm text-gray-400">
                Audit-ready ABA practice management built for compliance.
              </p>
            </div>

            <div>
              <h3 className="text-sm font-semibold mb-4">Product</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><Link to="/features" className="hover:text-white">Features</Link></li>
                <li><Link to="/billing" className="hover:text-white">Billing</Link></li>
                <li><Link to="/pricing" className="hover:text-white">Pricing</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-semibold mb-4">Compliance</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><Link to="/compliance" className="hover:text-white">Audit Defense</Link></li>
                <li><Link to="/ai-governance" className="hover:text-white">AI Governance</Link></li>
                <li><Link to="/security" className="hover:text-white">Security</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-semibold mb-4">Company</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><Link to="/contact" className="hover:text-white">Contact</Link></li>
                <li><span className="text-gray-500">BAA Available</span></li>
              </ul>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-400">
              &copy; {new Date().getFullYear()} Grace Alone ABA. All rights reserved.
            </p>
            <div className="flex gap-4 text-sm text-gray-400">
              <span>HIPAA Compliant</span>
              <span>SOC 2 Type II</span>
              <span>BAA Available</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
